/**
 * Constellation Drawing Utilities
 * 
 * Helper functions for rendering the constellation visualization
 * 
 * Visual Polish Pack v1:
 * - Nebula drift background
 * - Film grain overlay
 * - Parallax depth on background stars
 * - Star twinkling
 * - Node breathing effect
 * - Line shimmer/energy
 */

import {
  drawNebula,
  drawGrain,
  drawBackgroundStarsWithParallax,
  getNodeBreathing,
  getLineEnergy,
  getStarShimmer
} from './visualEffects';

/**
 * Get star radius based on connection type
 */
export function getStarRadius(node) {
  if (node.connection_type === 'bonded') return 8;
  if (node.connection_type === 'strong') return 6;
  return 4; // light
}

/**
 * Get star color and glow based on connection type
 */
export function getStarColor(node) {
  if (node.connection_type === 'bonded') {
    return {
      fill: '#A06BFF', // Purple
      glowAlpha: 0.4
    };
  }
  if (node.connection_type === 'strong') {
    return {
      fill: '#FFD700', // Gold
      glowAlpha: 0.3
    };
  }
  // light
  return {
    fill: '#E5E7EB', // Soft white/gray
    glowAlpha: 0.15
  };
}

/**
 * Get line style based on edge connection type
 */
export function getLineStyle(edge) {
  if (edge.connection_type === 'bonded') {
    return {
      color: '#A06BFF',
      width: 2,
      alpha: 0.6
    };
  }
  if (edge.connection_type === 'strong') {
    return {
      color: '#FFD700',
      width: 1.5,
      alpha: 0.4
    };
  }
  // light
  return {
    color: '#9CA3AF',
    width: 1,
    alpha: 0.25
  };
}

/**
 * Draw a halo ring around matched nodes (can chat)
 * Static effect - respects Power Saver / Reduce Motion automatically
 */
function drawMatchedHalo(ctx, x, y, r, theme) {
  ctx.save();
  
  // Subtle outer ring - purple-ish accent
  const accentColor = theme?.accent || 'rgba(192, 132, 252, 0.9)';
  
  ctx.globalAlpha = 0.55;
  ctx.lineWidth = Math.max(2, r * 0.18);
  ctx.strokeStyle = accentColor;
  
  ctx.beginPath();
  ctx.arc(x, y, r + Math.max(3, r * 0.25), 0, Math.PI * 2);
  ctx.stroke();
  
  // Faint outer ring (extra "premium" without heavy cost)
  ctx.globalAlpha = 0.18;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(x, y, r + Math.max(6, r * 0.45), 0, Math.PI * 2);
  ctx.stroke();
  
  ctx.restore();
}

/**
 * Draw a small green dot badge for matched nodes (optional extra clarity)
 */
function drawMatchedDot(ctx, x, y, r) {
  ctx.save();
  ctx.globalAlpha = 0.95;
  ctx.fillStyle = 'rgba(34, 197, 94, 0.95)'; // green dot = "available"
  const dotR = Math.max(2.5, r * 0.22);
  ctx.beginPath();
  ctx.arc(x + r * 0.65, y - r * 0.65, dotR, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * Draw a single node star with breathing and shimmer effects
 * Now includes matched halo for nodes with match_id
 */
export function drawNode(ctx, x, y, node, theme, time = 0, nodeIndex = 0) {
  const themeMultiplier = theme?.nodes?.baseRadius || 1.0;
  const glowMultiplier = theme?.fx?.glow || 0.35;
  
  // Apply breathing effect (subtle size pulse)
  const breathingScale = getNodeBreathing(time, nodeIndex, theme);
  
  // Apply shimmer effect (glow pulse)
  const shimmerMultiplier = getStarShimmer(time, nodeIndex, theme);
  
  const r = getStarRadius(node) * themeMultiplier * breathingScale;
  const { fill, glowAlpha } = getStarColor(node);
  
  // Draw matched halo FIRST (so node sits on top)
  const isMatched = !!node.match_id;
  if (isMatched) {
    drawMatchedHalo(ctx, x, y, r, theme);
  }

  // Draw glow with theme intensity + shimmer
  if (glowAlpha > 0) {
    ctx.save();
    ctx.fillStyle = fill;
    ctx.globalAlpha = glowAlpha * glowMultiplier * shimmerMultiplier;
    ctx.beginPath();
    ctx.arc(x, y, r * 2.2 * (theme?.nodes?.aura || 0.5), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Draw core circle
  ctx.save();
  ctx.fillStyle = fill;
  ctx.globalAlpha = 1;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  
  // Draw matched dot LAST (on top of everything)
  if (isMatched) {
    drawMatchedDot(ctx, x, y, r);
  }
}

/**
 * Draw the core star (current user) with breathing effect
 */
export function drawCoreStar(ctx, x, y, coreStar, theme, time = 0) {
  const themeMultiplier = theme?.nodes?.baseRadius || 1.0;
  const glowMultiplier = theme?.fx?.glow || 0.35;
  
  // Core star has its own breathing rhythm (slightly different)
  const pulseIntensity = theme?.motion?.pulse || 0.15;
  const coreBreath = pulseIntensity > 0 
    ? 0.98 + (Math.sin(time * 0.001) * 0.5 + 0.5) * 0.04 * pulseIntensity
    : 1.0;
  
  const r = 12 * themeMultiplier * coreBreath;
  const color = coreStar.aura_color || '#A06BFF';

  // Outer glow (large) with theme intensity + subtle pulse
  const glowPulse = pulseIntensity > 0
    ? 0.9 + (Math.sin(time * 0.0008 + 0.5) * 0.5 + 0.5) * 0.2 * pulseIntensity
    : 1.0;
  
  ctx.save();
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.2 * glowMultiplier * glowPulse;
  ctx.beginPath();
  ctx.arc(x, y, r * 3.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Middle glow with theme intensity
  ctx.save();
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.4 * glowMultiplier * glowPulse;
  ctx.beginPath();
  ctx.arc(x, y, r * 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Inner core
  ctx.save();
  ctx.fillStyle = color;
  ctx.globalAlpha = 1;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * Draw subtle background stars for ambiance
 */
export function drawBackgroundStars(ctx, width, height, count = 50) {
  ctx.save();
  ctx.fillStyle = '#FFFFFF';
  
  // Use deterministic positioning based on canvas size
  for (let i = 0; i < count; i++) {
    const x = (i * 137.5 % width); // Golden angle for nice distribution
    const y = (i * 73.3 % height);
    const size = 0.5 + (i % 3) * 0.3;
    const alpha = 0.1 + (i % 4) * 0.1;
    
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fill();
  }
  
  ctx.restore();
}

/**
 * Main drawing function - renders the entire constellation with visual effects
 * 
 * Visual Polish Pack v1 features:
 * - Nebula drift background (theme-dependent)
 * - Parallax depth on background stars
 * - Star twinkling animation
 * - Node breathing effect
 * - Line shimmer/energy
 * - Film grain overlay (subtle)
 */
export function drawConstellation(
  ctx,
  width,
  height,
  data,
  scale,
  offset,
  theme,
  time = 0 // Animation time (performance.now())
) {
  const { coreStar, nodes, edges } = data;
  
  // Default theme if not provided
  const effectiveTheme = theme || {
    id: 'classic',
    bg: { nebula: false, starfieldDensity: 80, grain: 0.05 },
    fx: { glow: 0.35, shimmer: 0.0 },
    motion: { twinkle: 0.15, drift: 0.05, pulse: 0.15 },
    lines: { opacity: 0.55, width: 1.0, energy: 0.0 },
    nodes: { baseRadius: 1.0, aura: 0.5 }
  };

  // Clear canvas with black background
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, width, height);

  // 1. Draw nebula background (slow drift animation)
  drawNebula(ctx, width, height, time, effectiveTheme);

  // 2. Draw background stars with parallax depth
  drawBackgroundStarsWithParallax(ctx, width, height, offset, time, effectiveTheme);

  // Calculate center point with offset
  const cx = width / 2 + offset.x;
  const cy = height / 2 + offset.y;

  // 3. Draw edges (lines from core to nodes) with energy/shimmer
  ctx.save();
  edges.forEach((edge, edgeIndex) => {
    if (edge.from_user_id !== coreStar.user_id) return;
    const node = nodes.find(n => n.user_id === edge.to_user_id);
    if (!node) return;

    const { x, y } = node;
    const px = cx + x * (width / 2) * 0.7 * scale; // 0.7 = padding factor
    const py = cy + y * (height / 2) * 0.7 * scale;

    const style = getLineStyle(edge);
    
    // Apply line energy effect
    const energy = getLineEnergy(time, edgeIndex, effectiveTheme);
    
    ctx.strokeStyle = style.color;
    ctx.lineWidth = style.width * effectiveTheme.lines.width * energy.width;
    ctx.globalAlpha = style.alpha * effectiveTheme.lines.opacity * energy.alpha;

    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(px, py);
    ctx.stroke();
  });
  ctx.restore();

  // 4. Draw connection nodes with breathing and shimmer effects
  nodes.forEach((node, nodeIndex) => {
    const px = cx + node.x * (width / 2) * 0.7 * scale;
    const py = cy + node.y * (height / 2) * 0.7 * scale;
    drawNode(ctx, px, py, node, effectiveTheme, time, nodeIndex);
  });

  // 5. Draw core star on top (so it's always visible)
  drawCoreStar(ctx, cx, cy, coreStar, effectiveTheme, time);

  // 6. Draw film grain overlay (very subtle)
  drawGrain(ctx, width, height, time, effectiveTheme);
}
